import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from '@fluentui/react/lib/Link';
import { Icon } from '@fluentui/react/lib/Icon';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ComposeStyles from '../utils/ComposeStyles';
import loc from 'owa-localize';
import {
    quickComposeOpenCollabSpace,
    quickComposeAddCollabSpace,
} from './QuickCompose.locstring.json';
import { ControlIcons } from 'owa-control-icons';
import { isEventFromDifferentMailbox } from 'owa-calendar-collaborative-space';
import { trace } from 'owa-trace';
import { logUsage } from 'owa-analytics';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';

interface QuickComposeCollabSpaceRowProps {
    event: CalendarEvent;
    composeStyles: ComposeStyles;
    launchFullCompose: (createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps) => void;
    customClassName?: string;
}

const styles = require('./QuickComposeContent.scss');
const classNames = require('classnames');

export default observer(function QuickComposeCollabSpaceRow(
    props: QuickComposeCollabSpaceRowProps
) {
    const { event, composeStyles, launchFullCompose, customClassName } = props;

    if (isEventFromDifferentMailbox(event)) {
        return null;
    }

    let linkText = quickComposeAddCollabSpace;
    if (event.CollabSpace) {
        let shouldShowCollabSpaceLink = false;
        try {
            const parsedCollabSpace = JSON.parse(event.CollabSpace);
            if (parsedCollabSpace) {
                const { location, metadata } = parsedCollabSpace;
                if (location && metadata?.state === 'Enabled') {
                    linkText = quickComposeOpenCollabSpace;
                    shouldShowCollabSpaceLink = true;
                }
            }
        } catch (e) {
            trace.warn('Unable to parse collab space in calendar event: ' + event.CollabSpace);
        } finally {
            if (!shouldShowCollabSpaceLink) {
                return null;
            }
        }
    }

    const showCollabSpaceInComposeForm = e => {
        if (!event.CollabSpace) {
            logUsage('CollabSpaceAddedFromPeekLink', undefined, { isCore: true });
            const createCollabSpaceOnInitProps: CreateCollabSpaceOnInitProps = {
                shouldCreateCollabSpace: true,
                collabSpaceCreationSource: 'QuickCompose',
            };
            launchFullCompose(createCollabSpaceOnInitProps);
        } else {
            logUsage('CollabSpaceOpenedFromPeekLink', undefined, { isCore: true });
            launchFullCompose();
        }
    };

    return (
        isFluidEnabledForSource(FluidOwaSource.CalendarCompose) && (
            <div className={classNames(styles.composeRow, styles.collabSpaceRow, customClassName)}>
                <Icon iconName={ControlIcons.SharedNotes} className={styles.collabSpaceIcon} />
                <Link
                    styles={composeStyles.linkGetStyles}
                    onClick={showCollabSpaceInComposeForm}
                    className={classNames(styles.collabSpaceLink, composeStyles.cssVariabelsStyle)}>
                    {loc(linkText)}
                </Link>
            </div>
        )
    );
});
